import type {RefObject} from "react";
import {ScrollClearance} from "consent/CookieBanner.styles";
import {UpArrowIcon} from "home/components/icons";
import {useMessages} from "i18n";
import {Pill} from "./BackToTopButton.styles";
import {useBackToTop} from "./useBackToTop";

/**
 * The "Back to top" button (kitecorp/transferify-ui#4043, owner decision 2026-11-02). When it shows and what a press does
 * are `pricing/backToTop`'s rules; this renders the pill and owns nothing else. Mounted by {@link ./PricingPage} and
 * never by the shared `PricingContent`, because the pricing dialog scrolls a sheet of its own and gets no button.
 *
 * A floating corner must not take away what it floats over, which is why it comes with two kinds of room while it is on
 * the page. The footer grows below its links (`useBackToTop`), so the page's last row can scroll clear of the pill. And
 * {@link ScrollClearance}, the cookie banner's own rule, stops a control that a keyboard scrolls to above the pill
 * rather than at the window's edge under it.
 *
 * The visible words are the accessible name, so the arrow says nothing to a screen reader.
 */
export function BackToTopButton({focusTarget}: {focusTarget: RefObject<HTMLElement | null>}) {
	const {pricing} = useMessages();
	const {visible, clearance, setButton, goToTop} = useBackToTop(focusTarget);
	if (!visible) return null;
	return (
		<>
			<ScrollClearance clearance={clearance}/>
			<Pill ref={setButton} type="button" onClick={goToTop}>
				<UpArrowIcon size={17}/>
				{pricing.backToTop}
			</Pill>
		</>
	);
}
